// src/components/ApplicationHistory.js
import React from 'react';
import PropTypes from 'prop-types';
import {
    FaBookOpen, FaSpinner, FaExclamationTriangle, FaFileUpload,
    FaCheckCircle, FaHourglassHalf // <<< Added missing icons
} from 'react-icons/fa';

// --- Helper definitions remain ---
const renderLoading = (text = "Loading...", styles = {}) => (
    <div style={styles.loadingIndicator || { padding: '2rem', textAlign: 'center' }}>
        <FaSpinner className="fa-spin" style={{ marginRight: '0.5rem' }} /> {text}
    </div>
);

const renderError = (errorText, styles = {}) => (
    <div style={styles.errorIndicator || { padding: '1rem', margin: '1rem 0', color: 'red', border: '1px solid red' }}>
        <FaExclamationTriangle style={{ marginRight: '0.5rem' }} /> Error: {errorText || 'Unknown error'}
    </div>
);

// Updated component using Card View
const ApplicationHistory = ({ isLoading, error, historyData, onViewBooking, styles }) => {

    // --- Render Single History Card ---
    const renderHistoryCard = (booking) => {
        const cardKey = booking.order_item_id ?? booking.order_id ?? `fallback-${Math.random()}`;
        if (!booking.order_item_id) {
            console.warn(`[ApplicationHistory] Missing 'order_item_id' for booking card. Using key: ${cardKey}. Booking:`, booking);
        }

        // --- START: Conditional Button Logic ---
        // Define statuses where the user *needs* to take action via the button
        // (Includes assignment trigger and document upload/re-upload)
        const actionableStatuses = [
            'paid',                 // Needs assignment triggered by button
            'pending information',
            'pending applicant info',  // Likely needs assignment/docs
            'pending documents',    // Needs upload
            'rejected'              // Needs re-upload
        ];

        // Define statuses where documents are submitted or processing is underway
        const processingStatuses = [
            'documents submitted',
            'processing',
            'approved', // Treat 'Approved' as still potentially processing/under review
        ];

        // Define statuses where the process is finished
        const completedStatuses = [
            'completed',
            // Add other final statuses like 'Cancelled' if needed
        ];

        // Use the item-specific status (now aliased as 'status' from GET /api/orders)
        const currentStatusLower = booking.status?.toLowerCase() || 'unknown'; // Handle potential null/undefined status
        const isActionable = actionableStatuses.includes(currentStatusLower);
        const isProcessing = processingStatuses.includes(currentStatusLower);
        const isCompleted = completedStatuses.includes(currentStatusLower);

        // Determine if the button should be disabled
        // Disable if it's NOT actionable AND it's not 'cart' (which shouldn't appear here anyway)
        // Also disable if the essential order_item_id is missing
        const isDisabled = (!isActionable && currentStatusLower !== 'cart') || !booking.order_item_id;

        // Determine button text, icon, and title based on the status
        let buttonText = "Documents"; // Default/Actionable state
        let buttonIcon = <FaFileUpload style={{ marginRight: '0.4rem' }}/>;
        let buttonTitle = "Assign Applicant / Upload Documents"; // Default/Actionable title

        if (isProcessing) {
            buttonText = "Processing";
            buttonIcon = <FaHourglassHalf style={{ marginRight: '0.4rem' }} />;
            buttonTitle = "Documents submitted, currently processing";
        } else if (isCompleted) {
            buttonText = "Completed";
            buttonIcon = <FaCheckCircle style={{ marginRight: '0.4rem' }} />;
            buttonTitle = "Application completed";
        } else if (!isActionable && !isProcessing && !isCompleted) {
             // Catch-all for other non-actionable states (e.g., 'unknown', or maybe future statuses)
             buttonText = "View Status"; // Generic non-action text
             buttonIcon = <FaFileUpload style={{ marginRight: '0.4rem' }}/>; // Keep generic icon or change
             buttonTitle = `Current status: ${booking.status || 'N/A'}`;
             // isDisabled is already true based on the logic above
        }
        // If actionable, defaults set above are used.

        // Style for Disabled Button
        const disabledButtonStyle = isDisabled ? (styles.cardActionButtonDisabled || { opacity: 0.6, cursor: 'not-allowed' }) : {};
        // --- END: Conditional Button Logic ---


        return (
            // Apply card style from props
            <div key={cardKey} style={styles.card || { border: '1px solid #ccc', borderRadius: '8px', padding: '1rem', marginBottom: '1rem', backgroundColor: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                {/* Card Content */}
                <div style={styles.cardBody || { display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {/* Service Name */}
                    <div style={styles.cardField || { display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={styles.cardLabel || { fontWeight: 'bold', color: '#555', fontSize: '0.9rem' }}>Service:</span>
                        <span style={styles.cardValue || { fontSize: '1rem', fontWeight: '500', color: '#333' }}>{booking.service_name || 'N/A'}</span>
                    </div>

                    {/* Date */}
                    <div style={styles.cardField || { display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={styles.cardLabel || { fontWeight: 'bold', color: '#555', fontSize: '0.9rem' }}>Date:</span>
                        <span style={styles.cardValue || { fontSize: '1rem' }}>{booking.date ? new Date(booking.date).toLocaleDateString() : 'N/A'}</span>
                    </div>

                    {/* Price Paid */}
                    <div style={styles.cardField || { display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={styles.cardLabel || { fontWeight: 'bold', color: '#555', fontSize: '0.9rem' }}>Price Paid:</span>
                        <span style={styles.cardValue || { fontSize: '1rem' }}>{booking.price_paid != null && booking.price_paid > 0 ? `₹${booking.price_paid}` : 'Free'}</span>
                    </div>

                    {/* Status */}
                    <div style={styles.cardField || { display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={styles.cardLabel || { fontWeight: 'bold', color: '#555', fontSize: '0.9rem' }}>Status:</span>
                        {/* Use statusBadge style based on the item-specific status */}
                        <span style={styles.statusBadge ? styles.statusBadge(booking.status) : { padding: '0.3rem 0.6rem', borderRadius: '4px', backgroundColor: '#eee' }}>
                            {booking.status || 'N/A'} {/* Display the item status */}
                        </span>
                    </div>

                    {/* --- MODIFIED Action Button --- */}
                    <div style={styles.cardActions || { borderTop: '1px solid #eee', marginTop: '1rem', paddingTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                            title={buttonTitle} // Dynamic title
                            style={{
                                ...(styles.cardActionButton || styles.viewBookingBtn), // Base style
                                ...disabledButtonStyle // Apply disabled styles conditionally
                            }}
                            onClick={() => {
                                if (!isDisabled && booking.order_item_id) { // Only trigger if not disabled and has ID
                                    onViewBooking(booking); // Pass the specific booking object
                                } else {
                                    // Optional: Log or toast why it's disabled
                                    console.log(`[AppHistory] Button disabled for item ${booking.order_item_id}, status: ${booking.status}`);
                                }
                            }}
                            disabled={isDisabled} // Disable the button based on status/ID
                             // Apply hover effects only if NOT disabled
                            onMouseEnter={(e) => {
                                if (!isDisabled) {
                                    const hoverStyle = styles.cardActionButtonHover || styles.viewBookingBtnHover;
                                    Object.assign(e.currentTarget.style, hoverStyle);
                                }
                             }}
                            onMouseLeave={(e) => {
                                // Reset potentially changed properties from hoverStyle
                                const baseStyle = styles.cardActionButton || styles.viewBookingBtn;
                                const hoverStyle = styles.cardActionButtonHover || styles.viewBookingBtnHover || {};
                                Object.keys(hoverStyle).forEach(key => {
                                     // Check if baseStyle has the property before resetting
                                     if (baseStyle && baseStyle.hasOwnProperty(key)) {
                                        e.currentTarget.style[key] = baseStyle[key];
                                    } else {
                                        // If base doesn't have it, reset to default/initial state (might be tricky without CSS classes)
                                        e.currentTarget.style[key] = ''; // Or null? Test what works best
                                    }
                                });
                                // Re-apply base styles and disabled styles
                                Object.assign(e.currentTarget.style, baseStyle);
                                Object.assign(e.currentTarget.style, disabledButtonStyle);
                            }}
                        >
                            {buttonIcon} {buttonText} {/* Dynamic icon and text */}
                        </button>
                    </div>
                    {/* --- END MODIFIED Action Button --- */}
                </div>
            </div>
        );
    };

    // --- Main Return Logic Updated for Cards ---
    return (
        <section style={styles.section || {}}>
            <h2 style={styles.sectionTitle || {}}> <FaBookOpen style={{ marginRight: '0.5rem'}} /> Your Application History </h2>
            <div style={styles.cardsContainer || { display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {Array.isArray(historyData) && historyData.length > 0
                    ? ( historyData.map(renderHistoryCard) )
                    : (
                        <div style={styles.noDataMessage || { textAlign: 'center', padding: '2rem', color: '#777', fontStyle: 'italic', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                            No past applications found.
                        </div>
                      )
                }
            </div>
        </section>
    );
};

// --- PropTypes remain the same ---
ApplicationHistory.propTypes = {
    isLoading: PropTypes.bool.isRequired,
    error: PropTypes.string,
    historyData: PropTypes.array.isRequired, // Now expects 'status' to be the item application status
    onViewBooking: PropTypes.func.isRequired,
    styles: PropTypes.object.isRequired,
};

export default ApplicationHistory;